import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface User {
    email: string;
    name?: string;
    picture?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    login: (googleToken: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://gad-alchemist-api.backtrue.workers.dev';

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // 檢查 localStorage 中是否有 token
    useEffect(() => {
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
            try {
                const decoded: any = jwtDecode(storedToken);
                // 檢查 token 是否過期
                if (decoded.exp && decoded.exp * 1000 > Date.now()) {
                    setToken(storedToken);
                    setUser({
                        email: decoded.email,
                        name: decoded.name,
                        picture: decoded.picture
                    });
                } else {
                    // Token 過期，清除
                    localStorage.removeItem('auth_token');
                }
            } catch (error) {
                console.error('Invalid token:', error);
                localStorage.removeItem('auth_token');
            }
        }
        setIsLoading(false);
    }, []);

    const login = async (googleToken: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: googleToken }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || '登入失敗');
            }

            const data = await response.json();
            
            // 儲存 token 和使用者資訊
            localStorage.setItem('auth_token', data.token);
            setToken(data.token);
            setUser(data.user);
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const logout = () => {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, token, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
